import type React from 'react';

import { Button } from '@change/ds-core-ui';

import { TypedTranslate } from '@change-corgi/core/react/i18n';

import { DynamicTask } from './components/DynamicTask';
import { DynamicTasksLoading } from './components/DynamicTasksLoading';
import { SectionHeader } from './components/SectionHeader';
import { PERSONALIZED_TASKS_GENERATION_STATUSES } from './constants';
import { useDynamicTasks } from './hooks/useDynamicTasks';
import { useTaskExpansion } from './hooks/useTaskExpansion';

export const DynamicTasks: React.FC = () => {
	const { data, actions } = useDynamicTasks();
	const {
		tasks,
		stats,
		generationStatus,
		showTaskListToggle,
		toggleTaskListLabel,
		toggleTaskListRemainingCount,
		toggleTaskListIcon,
	} = data;

	const { toggleTaskList } = actions;

	const { isExpanded, toggleExpansion } = useTaskExpansion(0);

	// TODO: REMOVE this stats.count check once the server is updating the generationStatus correctly for users with no tasks.
	// This is a temporary fix to prevent showing an empty state with a "View More" button for users with no tasks.
	if (stats.count <= 0) {
		if (
			generationStatus === PERSONALIZED_TASKS_GENERATION_STATUSES.PENDING ||
			(generationStatus === PERSONALIZED_TASKS_GENERATION_STATUSES.IN_PROGRESS && stats.count === 0)
		) {
			return <DynamicTasksLoading />;
		}
	}

	if (generationStatus === PERSONALIZED_TASKS_GENERATION_STATUSES.FAILED || stats.count === 0) {
		return null;
	}

	return (
		<div className="bg-background-base flex flex-col gap-5 px-4 py-8 md:p-12" data-qa="dynamic-tasks">
			<SectionHeader completed={stats.completed} total={stats.count} />
			<div className="flex flex-col gap-4">
				<div className="flex flex-col">
					{tasks.map((task) => (
						<DynamicTask
							key={task.index}
							task={task}
							isExpanded={isExpanded(task.index)}
							onToggleExpand={() => toggleExpansion(task.index)}
						/>
					))}
				</div>
				{showTaskListToggle && (
					<Button
						variant="tertiary"
						size="x-small"
						onClick={toggleTaskList}
						data-qa="dynamic-tasks-view-more-button"
						className="bg-fill-transparent text-typography-strong text-size-body-small w-full gap-2"
					>
						<TypedTranslate
							value={toggleTaskListLabel}
							replacements={{ count: String(toggleTaskListRemainingCount) }}
						/>
						{toggleTaskListIcon}
					</Button>
				)}
			</div>
		</div>
	);
};
